"use client";

import {
  getAccessToken,
  TREX_STORED_LOGIN_CHANGED_EVENT,
} from "@trex-2b/libs/config";
import { useCallback, useSyncExternalStore } from "react";
import { showModalAuthLogin } from "./modal-auth-login";

function readHasAccessToken(): boolean {
  return Boolean(getAccessToken());
}

function subscribeLoginState(onStoreChange: () => void) {
  window.addEventListener(TREX_STORED_LOGIN_CHANGED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(TREX_STORED_LOGIN_CHANGED_EVENT, onStoreChange);
  };
}

export function useLandingLoginGuard() {
  const isLoggedIn = useSyncExternalStore(
    subscribeLoginState,
    readHasAccessToken,
    () => false,
  );

  const syncLoggedInState = useCallback(() => {
    window.dispatchEvent(new Event(TREX_STORED_LOGIN_CHANGED_EVENT));
  }, []);

  const showLogin = useCallback(
    (onLoginSuccess?: () => void) => {
      showModalAuthLogin({
        onLoginSuccess: () => {
          syncLoggedInState();
          onLoginSuccess?.();
        },
      });
    },
    [syncLoggedInState],
  );

  const runWithLogin = useCallback(
    (action: () => void | Promise<void>) => {
      const executeAction = () => {
        Promise.resolve(action()).catch((error) => {
          console.error("Action after login failed", error);
        });
      };

      if (readHasAccessToken()) {
        syncLoggedInState();
        executeAction();
        return;
      }

      showLogin(executeAction);
    },
    [showLogin, syncLoggedInState],
  );

  return {
    isLoggedIn,
    showLogin,
    runWithLogin,
    syncLoggedInState,
  };
}
